// ============================================================================
// lib/site/i18n.ts — локализация сайтов (Этап 1). Значение props/SEO:
// строка ИЛИ {"ru":"…","en":"…"}; резолв: локаль → default_locale (политика
// 'default', как на сервере — единый инвариант). URL: default-локаль без
// префикса, остальные — /p/{site}/{locale}/{slug}.
// ============================================================================

export type LocalizedValue = string | Record<string, string> | null | undefined;

/** FieldBinding — биндинг блока страницы ОБЪЕКТА (Этап 2): {field:"column_name"}
 *  → значение из record (данные записи, SSoT — сервер, GetPublicPage.record).
 *  Смешанные формы запрещены явной схемой: объект с ключом field — ТОЛЬКО
 *  биндинг (локальные ключи внутри него не рассматриваются). */
export interface FieldBinding { field: string }

/** PLACEHOLDER_COL — колонка плейсхолдера {column_name} в строке (хвосты §2):
 *  ЛЮБОЙ текст (шапка/меню/подвал/блоки/SEO) может содержать {column_name} —
 *  подстановка значениями записи страницы объекта. На не-объектных страницах
 *  (record нет) — пустая подстановка, «{field}» в вывод НЕ попадает. */
const PLACEHOLDER_COL = /\{([A-Za-z0-9_]+)\}/g;

/** substituteRecord — подстановка {column_name} значениями записи.
 *  record нет / колонки нет → '' (детерминированно). */
export function substituteRecord(text: string, record?: Record<string, string>): string {
  if (!text.includes('{')) return text;
  return text.replace(PLACEHOLDER_COL, (_m, name: string) => (record && record[name]) || '');
}

/** t — значение на локали (fallback — локаль по умолчанию; иначе '').
 *  record — данные записи страницы объекта: биндинг {field} резолвится из неё
 *  (отсутствующая колонка → '', блок не падает); в строках подставляются
 *  плейсхолдеры {column_name}. */
export function t(value: LocalizedValue, locale: string, defaultLocale: string, record?: Record<string, string>): string {
  if (value == null) return '';
  if (typeof value === 'string') return substituteRecord(value, record);
  const binding = value as { field?: unknown };
  if (typeof binding.field === 'string') {
    return (record && record[binding.field]) || '';
  }
  const byLocale = value[locale];
  if (typeof byLocale === 'string' && byLocale !== '') return substituteRecord(byLocale, record);
  const byDefault = value[defaultLocale];
  if (typeof byDefault === 'string' && byDefault !== '') return substituteRecord(byDefault, record);
  return '';
}

/** isFieldBinding — значение-биндинг записи (для редактора свойств). */
export function isFieldBinding(value: LocalizedValue): value is FieldBinding & Record<string, string> {
  return !!value && typeof value === 'object' && typeof (value as { field?: unknown }).field === 'string';
}

/** tSite — текст сайта: props → словарь переводов `site.{code}.{key}` (Этап 3).
 *  Перевод применяется, когда значение props пустое (реестр «Переводы»). */
export function tSite(
  value: LocalizedValue,
  locale: string,
  defaultLocale: string,
  key: string,
  translations?: Record<string, string>,
  record?: Record<string, string>,
): string {
  const direct = t(value, locale, defaultLocale, record);
  if (direct) return direct;
  return (translations && translations[key]) || '';
}

/** parseSiteSettings — настройки сайта (JSON): header/footer/menus/tokens/seo. */
export interface SiteRobotsSettings {
  userAgent?: string;
  allow?: string[];
  disallow?: string[];
  sitemap?: string;
}

/** SiteSearchTarget — цель поиска по сайту (Этап 3, whitelist). */
export interface SiteSearchTarget {
  objectType: string;   // catalog | document
  objectName: string;   // код справочника/документа
  fields: string[];     // поля LIKE-поиска (column_name)
  titleField?: string;  // поле заголовка результата
  urlTemplate: string;  // '{ref}' → код/номер записи
}

export interface SiteSettings {
  tokens?: { primary?: string; text?: string; bg?: string; maxWidth?: number; radius?: number };
  header?: { enabled?: boolean; title?: LocalizedValue; logo?: string; showMenu?: boolean };
  footer?: { enabled?: boolean; text?: LocalizedValue };
  menus?: Record<string, { label?: LocalizedValue; slug?: string; pageCode?: string; url?: string }[]>;
  seo?: {
    title?: LocalizedValue;
    description?: LocalizedValue;
    ogImage?: string;
    robots?: SiteRobotsSettings;
    /** Видимость сайта целиком (Этап 3): '' | all | auth | role:<код>. */
    visibility?: string;
  };
  /** Поиск по сайту (Этап 3): только эти цели доступны публичному поиску. */
  search?: { targets?: SiteSearchTarget[] };
}

export function parseSiteSettings(json: string): SiteSettings {
  if (!json || !json.trim()) return {};
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as SiteSettings) : {};
  } catch {
    return {};
  }
}

/** pageHref — URL страницы сайта (default-локаль без префикса). */
export function pageHref(siteCode: string, locale: string, defaultLocale: string, slug: string): string {
  let url = `/p/${siteCode}`;
  if (locale && locale !== defaultLocale) url += `/${locale}`;
  if (slug) url += `/${slug.replace(/^\/+|\/+$/g, '')}`;
  return url;
}

/** resolveSitePath — из pathname после /p/{site} получить локаль и slug. */
export function resolveSitePath(rest: string, locales: string[], defaultLocale: string): { locale: string; slug: string } {
  const parts = rest.split('/').filter(Boolean);
  let locale = defaultLocale;
  if (parts.length > 0 && locales.includes(parts[0])) {
    locale = parts[0];
    parts.shift();
  }
  return { locale, slug: parts.join('/') };
}
